// FormEditRichTextTemplate.jsx
import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  CircularProgress,
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { setSuccess } from "../../../Redux/AlertReducer";
import { useDispatch } from "react-redux";
import { Helper } from "../../../tools/Helper";
import { api_Routes } from "../../../tools/api_Routes";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

export const FormEditRichTextTemplate = ({ data }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [templateData, setTemplateData] = useState({
    name: "",
    subject: "",
    body: "",
    isHtml: true
  });

  const templateVariables = [
    { key: '{{user_name}}', description: 'User full name' },
    { key: '{{user_email}}', description: 'User email address' },
    { key: '{{user_id}}', description: 'User ID' },
    { key: '{{current_date}}', description: 'Current date (YYYY-MM-DD)' }
  ];

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  useEffect(() => {
    if (data) {
      setTemplateData({
        name: data.name || "",
        subject: data.subject || "",
        body: data.body || "",
        isHtml: data.is_html || true
      });
    }
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTemplateData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBodyChange = (value) => {
    setTemplateData((prev) => ({
      ...prev,
      body: value,
    }));
  };

  const handleVariableInsert = (variable) => {
    const quillEditor = document.querySelector('.ql-editor');
    if (quillEditor) {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      range.insertNode(document.createTextNode(variable));

      // Update React state to reflect the change
      const newBody = templateData.body + variable;
      setTemplateData(prev => ({ ...prev, body: newBody }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoadingSubmit(true);

    try {
      const payload = {
        name: templateData.name,
        subject: templateData.subject,
        body: templateData.body,
        is_html: templateData.isHtml,
        type: 'rich_text'
      };

      const { result, response, message } = await Helper.Post({
        url: `${api_Routes.EmailTemplates.Edit}${data.id}`,
        data: payload,
      });

      setIsLoadingSubmit(false);
      if (response) {
        dispatch(setSuccess("Email template updated successfully!"));
        navigate("/EmailTemplates");
      }
    } catch (error) {
      console.error("An error occurred during form submission:", error);
      setIsLoadingSubmit(false);
    }
  };

  return (
      <form onSubmit={handleSubmit}>
        <div className="p-6 max-w-4xl mx-auto">
          <Paper elevation={3} className="p-6">
            <Box className="space-y-6">
              <TextField
                  fullWidth
                  label="Template Name"
                  name="name"
                  required
                  value={templateData.name}
                  onChange={handleInputChange}
                  variant="outlined"
              />

              <TextField
                  fullWidth
                  label="Email Subject"
                  name="subject"
                  required
                  value={templateData.subject}
                  onChange={handleInputChange}
                  variant="outlined"
              />

              <div className="mb-4">
                <Typography variant="subtitle1" className="mb-2">Available Variables:</Typography>
                <div className="flex flex-wrap gap-2 mb-4">
                  {templateVariables.map((varItem) => (
                      <Chip
                          key={varItem.key}
                          label={`${varItem.key} - ${varItem.description}`}
                          onClick={() => handleVariableInsert(varItem.key)}
                          variant="outlined"
                          clickable
                      />
                  ))}
                </div>
                <Typography variant="body2" color="textSecondary">
                  Click on a variable to insert it into the template
                </Typography>
              </div>

              <Divider className="my-4" />

              <Typography variant="subtitle1" className="mb-2">Email Body:</Typography>
              <ReactQuill
                  theme="snow"
                  value={templateData.body}
                  onChange={handleBodyChange}
                  modules={modules}
                  className="border rounded"
                  style={{ height: '400px', marginBottom: '40px' }}
              />

              <FormControlLabel
                  control={
                    <Checkbox
                        checked={templateData.isHtml}
                        onChange={(e) => setTemplateData({...templateData, isHtml: e.target.checked})}
                    />
                  }
                  label="HTML Email"
              />

              <div className="flex justify-end space-x-4 pt-4">
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate(-1)}
                    size="large"
                >
                  Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoadingSubmit}
                    size="large"
                >
                  {isLoadingSubmit ? <CircularProgress size={24} /> : 'Update Template'}
                </Button>
              </div>
            </Box>
          </Paper>
        </div>
      </form>
  );
};